import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import AssaySchemaErrorList from 'results/assay-schemas/AssaySchemaErrorList';
import * as Colors from 'ui-library/Colors';

import AutomationFileConfigEditorHeader from '../AutomationFileConfigEditorHeader';
import {
  AutomationFileConfigListEditor,
  BaseAutomationFileConfigListEditorProps,
} from '../AutomationFileConfigListEditor';
import {AutomationInputFileConfigEditorProps} from '../AutomationFileConfigUtils';
import {RemoveComponentButton} from '../RunSchemaEditorActionButton';
import {AutomationInputFileConfigSpec} from '../RunSchemaSpecConstants';
import AutomationInputFileConfigEditorColumnsMapSection from './AutomationInputFileConfigEditorColumnsMapSection';
import AutomationInputFileConfigEditorFormatSection from './AutomationInputFileConfigEditorFormatSection';
import AutomationInputFileConfigEditorSourceDestSection from './AutomationInputFileConfigEditorSourceDestSection';
import AutomationInputFileConfigEditorTransformsSection from './AutomationInputFileConfigEditorTransformsSection';

export function AutomationInputFileConfigEditor(props: AutomationInputFileConfigEditorProps): JSX.Element {
  return (
    <div className={css(styles.editorContainer)}>
      <AssaySchemaErrorList errors={props.errors} component={`Input file configurations ${props.index}`} />
      <AutomationFileConfigEditorHeader
        automationFileConfig={props.inputFileConfig}
        description="This will be the section header for the input file in the run."
        onUpdateConfig={props.onUpdateConfig}
        isDuplicateName={props.isDuplicateName}
      />
      <AutomationInputFileConfigEditorSourceDestSection {...props} />
      <AutomationInputFileConfigEditorColumnsMapSection {...props} />
      <AutomationInputFileConfigEditorTransformsSection {...props} />
      <AutomationInputFileConfigEditorFormatSection {...props} />
      <RemoveComponentButton
        className={css(styles.removeEditorButton)}
        onClick={() => {
          props.onRemoveConfig();
        }}
        dataTestAttrs={{
          component: 'AutomationInputFileConfigEditor',
          element: 'remove',
          index: `${props.index}`,
        }}
      />
    </div>
  );
}

interface AutomationInputFileConfigListEditorProps extends BaseAutomationFileConfigListEditorProps {
  automationInputFileConfigs: Array<AutomationInputFileConfigSpec>;
}

export function AutomationInputFileConfigListEditor(
  props: AutomationInputFileConfigListEditorProps
): JSX.Element {
  const createInputFileConfig = (): AutomationInputFileConfigSpec => ({
    name: '',
    columns: [],
    sortColumns: [],
    rowConfigs: [],
    delimiter: ',',
    fileExtension: 'csv',
  });
  return (
    <AutomationFileConfigListEditor
      {...props}
      automationFileConfigs={props.automationInputFileConfigs}
      createNewConfig={createInputFileConfig}
      configEditorClass={AutomationInputFileConfigEditor}
      getConfigEditorProps={(config, existingConfigs) => ({
        inputFileConfig: config,
        existingInputFileConfigs: existingConfigs,
      })}
      title="Input file configurations"
      placeholder="Configure how Benchling generates your input files"
      addButtonLabel="Add input file configuration to run"
      dataTestAttrs={{component: 'AutomationInputFileConfigListEditor', element: 'root'}}
    />
  );
}

const styles = StyleSheet.create({
  editorContainer: {
    position: 'relative',
    width: '100%',
    overflowX: 'hidden',
    paddingBottom: '16px',
    marginBottom: '32px',
    paddingTop: '16px',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '4px',
  },
  editorHeader: {
    padding: '6px',
  },
  removeEditorButton: {
    position: 'absolute',
    zIndex: 30,
    top: '16px',
    right: '16px',
    border: 'none',
    background: 'none',
    float: 'right',
  },
});
